/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import IDate from "../../data/Date";
import IChild from "../../data/priest/Child";
import IPriestProfileData from "../../data/priest/PriestProfileData";
import IQualification from "../../data/priest/Qualification";
import ISpouse from "../../data/priest/Spouse";

const dateToString = (date: IDate) => {
    return date && date.year && date.month && date.day ? date.year + "." + date.month + "." + date.day : "";
};

const stringFromChildren = (children: IChild[]) => {
    return children ? children.map((child) => child.name).join(",") : "";
};

const stringFromSpouses = (spouses: ISpouse[]) => {
    return spouses ? spouses.map((spouse) => spouse.name).join(",") : "";
};

const stringFromQualifications = (qualifications: IQualification[]) => {
    if (!qualifications || qualifications.length === 0) return "";
    return qualifications
        .map((q) => {
            const date = q?.genesisDate ? dateToString(q.genesisDate) : "";
            const comment = q?.genesisDate?.comment || "";
            return `${q?.diplomaName || ""} | ${date} | ${comment}`.trim();
        })
        .join("\n");
};

const joinStrings = (arr?: any[]) => {
    return arr && arr.length > 0 ? arr.map((i) => (typeof i === "string" ? i : JSON.stringify(i))).join(",") : "";
};

const stringify = (v: any) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
    try {
        return JSON.stringify(v);
    } catch {
        return String(v);
    }
};

const placementsToString = (placements: any[]) => {
    if (!placements || placements.length === 0) return "";
    return placements
        .map((placement) => {
            const place = placement?.place || "";
            const start = dateToString(placement?.startDate) || "";
            const end = dateToString(placement?.endDate) || "";
            // produce "place|start|end" like tests expect
            return `${place}${start || end ? `|${start}|${end}` : ""}`.trim();
        })
        .join("\n");
};

const officesToString = (offices: any[]) => {
    if (!offices || offices.length === 0) return "";
    return offices
        .map((o) => {
            const name = o?.officeName || o?.name || "";
            const start = dateToString(o?.startDate) || "";
            const end = dateToString(o?.endDate) || "";
            return `${name}${start || end ? `|${start}|${end}` : ""}`.trim();
        })
        .join("\n");
};

export const mapToExportedPriestData = (priest: IPriestProfileData) => {
    const mainPlacements = placementsToString(priest.mainPriestPlaces);
    const assistantPlacements = placementsToString(priest.assistantPriestPlaces);

    return {
        ID: priest.ID,
        name: priest.name,
        verified: priest.verified,
        // birth / location
        birthTown: priest.birthTown,
        birthLocation: stringify(priest.birthLocation),
        birthCountry: priest.birthCountry,
        birthCounty: priest.birthCounty,
        birthDate: dateToString(priest.birthDate),
        birthDateComment: priest.birthDate?.comment || "",
        deathDate: dateToString(priest.deathDate),
        deathDateComment: priest.deathDate?.comment || "",
        subscriptionDate: dateToString(priest.subscriptionDate),
        subscriptionDateComment: priest.subscriptionDate?.comment || "",
        graduationDate: dateToString(priest.graduationDate),
        diocese: priest.diocese,
        // consecration
        consecrationDate: dateToString(priest.consecration?.date),
        consecrationPlace: priest.consecration?.place || "",
        // marital / family
        isMarried: priest.isMarried,
        isWidow: priest.isWidow,
        isDivorced: priest.isDivorced,
        father: stringify(priest.father),
        mother: stringify(priest.mother),
        retirementDate: dateToString(priest.retirementDate),
        children: stringFromChildren(priest.children),
        spouses: stringFromSpouses(priest.spouses),
        // qualifications / studies / religion
        qualifications: stringFromQualifications(priest.qualifications),
        studyStatus: priest.studyStatus,
        religion: priest.religion,
        // language skills
        speakingSkillsLess: priest.speakingSkills?.less || "",
        speakingSkillsMedium: priest.speakingSkills?.medium || "",
        speakingSkillsWell: priest.speakingSkills?.well || "",
        writingSkillsLess: priest.writingSkills?.less || "",
        writingSkillsMedium: priest.writingSkills?.medium || "",
        writingSkillsWell: priest.writingSkills?.well || "",
        // places, offices and related
        mainPlacements: mainPlacements,
        assistantPlacements: assistantPlacements,
        churchOffices: officesToString(priest.churchOffices),
        otherOffices: officesToString(priest.otherOffices),
        // other collections
        literaryWorks: joinStrings(priest.literaryWorks),
        priestReferences: joinStrings(priest.priestReferences),
        disciplinaryMatters: joinStrings(priest.disciplinaryMatters),
        suspensions: joinStrings(priest.suspensions),
        changedCareer: stringify(priest.changedCareer),
        uncertainFields: Array.isArray(priest.uncertainFields) ? priest.uncertainFields.join(",") : "",
        // files / pictures / notes / army / fired / resigned
        pictures: joinStrings(priest.pictures),
        files: joinStrings(priest.files),
        army: priest.army || "",
        resigned: dateToString(priest.resigned),
        fired: dateToString(priest.fired),
        note: priest.note || ""
    };
};

export default mapToExportedPriestData;
